/**
 * party — the canonical HP-bearing party member model.
 *
 * Single source of truth shared by the overworld, battle, save system, and
 * the party-facing UIs (SurggieCenter, EvolutionScreen, MoveLearnPrompt).
 * Shape is a superset of SaveData v2 party entries: saved objects (which lack
 * `types`/`stats`/`fainted`) are upgraded via `rehydratePartyMember`.
 */

import { SPECIES_DEX, type SpeciesData } from "@/data/species";
import { MOVES_DATA } from "@/data/moves";
import { resolveSpecies, movesForSpecies } from "@/engine/battle-participant";
import { newLoadout, type PluginLoadout } from "@/engine/plugin-system";

export interface PartyMember {
  /** Species sprite key, e.g. "surggie_3" (same as SaveData.party[].speciesId). */
  speciesId: string;
  nickname?: string;
  level: number;
  /** XP progress within the current level. */
  xp: number;
  hpCurrent: number;
  hpMax: number;
  moves: string[];
  pp: Record<string, number>;
  types: string[];
  stats: { atk: number; def: number; spd: number };
  fainted: boolean;
  loadout: PluginLoadout;
}

export interface MoveLearnRequest {
  partyIndex: number;
  moveId: string;
}

/** Max PP for each move id (catalog `pp`, falling back to 10 when unknown). */
export function fullPp(moves: string[]): Record<string, number> {
  const pp: Record<string, number> = {};
  for (const moveId of moves) {
    const move = MOVES_DATA.find((m) => m.id === Number.parseInt(moveId, 10));
    pp[moveId] = move?.pp ?? 10;
  }
  return pp;
}

function statsFor(species: SpeciesData): { atk: number; def: number; spd: number } {
  return { atk: species.baseAtk, def: species.baseDef, spd: species.baseSpe };
}

function typesFor(species: SpeciesData): string[] {
  return [species.type1, species.type2].filter((t): t is string => Boolean(t));
}

/**
 * Max HP for a species at `level`.
 *
 * Known species: `10 + floor(baseHp * level / 10)` — base-stat-driven so
 * tankier species (higher baseHp) pull ahead as they level.
 * Unknown species: the legacy `20 + level * 5` formula, keeping old saves and
 * unresolvable ids coherent with pre-party-model battles.
 */
export function maxHpFor(speciesIdOrKey: string, level: number): number {
  const species = resolveSpecies(speciesIdOrKey);
  if (!species) return 20 + level * 5;
  return 10 + Math.floor((species.baseHp * level) / 10);
}

/** XP required to advance from `level` to `level + 1`. */
export function xpToNext(level: number): number {
  return 20 + level * 15;
}

/**
 * Build a fresh, full-health party member. Unresolvable ids fall back to the
 * first species (mirrors buildParticipant) so a member can always be created.
 */
export function createPartyMember(
  speciesIdOrKey: string,
  level: number,
  nickname?: string,
): PartyMember {
  const species = resolveSpecies(speciesIdOrKey) ?? SPECIES_DEX[0];
  const moves = movesForSpecies(species, level);
  const hpMax = maxHpFor(species.sprite, level);

  return {
    speciesId: species.sprite,
    nickname,
    level,
    xp: 0,
    hpCurrent: hpMax,
    hpMax,
    moves,
    pp: fullPp(moves),
    types: typesFor(species),
    stats: statsFor(species),
    fainted: false,
    loadout: newLoadout(species.sprite),
  };
}

/**
 * Award XP. Pure — returns a new member, never mutates. Levels up while
 * xp >= xpToNext(level); hpMax is recomputed and hpCurrent grows by the same
 * delta so damage taken is preserved (fainted members stay at 0 HP). Stats
 * are re-derived from species base stats.
 */
export function applyXp(
  m: PartyMember,
  gained: number,
): { member: PartyMember; levelsGained: number } {
  let xp = m.xp + gained;
  let level = m.level;
  let levelsGained = 0;

  while (xp >= xpToNext(level)) {
    xp -= xpToNext(level);
    level++;
    levelsGained++;
  }

  if (levelsGained === 0) {
    return { member: { ...m, xp }, levelsGained: 0 };
  }

  const species = resolveSpecies(m.speciesId);
  const hpMax = maxHpFor(m.speciesId, level);
  const delta = hpMax - m.hpMax;
  const hpCurrent = m.fainted
    ? m.hpCurrent
    : Math.max(0, Math.min(m.hpCurrent + delta, hpMax));

  return {
    member: {
      ...m,
      level,
      xp,
      hpMax,
      hpCurrent,
      stats: species ? statsFor(species) : m.stats,
    },
    levelsGained,
  };
}

/**
 * Upgrade a SaveData v2 party entry (no `types`/`stats`/`fainted` fields) to a
 * full PartyMember. Trusts the saved hp/moves/pp; fills types/stats from the
 * species (first-species fallback when unresolvable, mirroring buildParticipant).
 */
export function rehydratePartyMember(saved: {
  speciesId: string;
  nickname?: string;
  level: number;
  xp: number;
  hpCurrent: number;
  hpMax: number;
  moves: string[];
  pp: Record<string, number>;
  loadout?: PluginLoadout;
}): PartyMember {
  const species = resolveSpecies(saved.speciesId) ?? SPECIES_DEX[0];

  return {
    speciesId: saved.speciesId,
    nickname: saved.nickname,
    level: saved.level,
    xp: saved.xp,
    hpCurrent: saved.hpCurrent,
    hpMax: saved.hpMax,
    moves: saved.moves,
    pp: saved.pp,
    types: typesFor(species),
    stats: statsFor(species),
    fainted: saved.hpCurrent <= 0,
    loadout: saved.loadout ?? newLoadout(saved.speciesId),
  };
}
